import { GlobalStore } from '@highlight/ipc'
import { useGlobalStore } from '@highlight/ipc/react'

/**
 * Where the tour stands for this user: `dismissed` and `completed` both mean
 * "never offer it again". They're kept distinct so a later entry point (the
 * Quick Tour's upcoming step, analytics) can tell a decline from a walk-through.
 *
 * `replay` is the dev reset: unseen, and the next meeting to end is treated as
 * a first meeting so the offer runs on an account that has recorded before. It
 * is a status rather than a flag beside one so that resetting is a single
 * store write -- as two writes, the status landing first re-ran the evaluation
 * without the override and dismissed the offer before it arrived.
 */
export type MeetingTourStatus = 'unseen' | 'replay' | 'dismissed' | 'completed'

/** Statuses that still allow an offer. */
export const isMeetingTourUnseen = (status: MeetingTourStatus) => status === 'unseen' || status === 'replay'

/**
 * In the user store rather than an `atomWithStorage`: the dev reset button
 * lives in the settings window, and `GlobalStore` is the cross-window store.
 */
export const MEETING_TOUR_STATUS_KEY = 'meeting-tour-status'

export function useMeetingTourStatus() {
  return useGlobalStore<MeetingTourStatus>('user', MEETING_TOUR_STATUS_KEY, 'unseen')
}

/** For write paths that outlive the component (unmount cleanup). */
export function setMeetingTourStatus(status: MeetingTourStatus) {
  GlobalStore.user().set(MEETING_TOUR_STATUS_KEY, status)
}
